import { dealerAdminHeaders, readDealerAdminSession } from './dealerAdminSession';

export const SUPPORT_SESSION_KEY = 'signalbridge.supportTenant';
export const SUPPORT_TENANT_HEADER = 'X-SignalBridge-Support-Tenant-Id';
export const PLATFORM_SUPPORT_DECLINED_KEY = 'signalbridge.supportTenant.declined';

const listeners = new Set();

function emit() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A listener cannot block logout or tenant switching.
    }
  });
}

export function subscribeSupportedTenantSession(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function readSupportedTenantSession() {
  try {
    const raw = sessionStorage.getItem(SUPPORT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const tenantId = String(parsed?.tenantId || '').trim();
    if (!tenantId) return null;
    return {
      tenantId,
      name: String(parsed?.name || '').trim(),
      accessLevel: parsed?.accessLevel === 'manage' ? 'manage' : 'view',
      algoDeviceSupport: parsed?.algoDeviceSupport === true,
      tenantAlgoEnabled: parsed?.tenantAlgoEnabled === true,
    };
  } catch {
    return null;
  }
}

const PREVIOUS_TENANT_KEY = 'signalbridge.supportTenant.previous';

const SUPPORT_READ_PREFIXES = [
  '/api/flows',
  '/api/actions',
  '/api/devices',
  '/api/v1/sites',
  '/api/users/list',
  '/api/v2/inbound-rules',
  '/api/v2/triggers',
  '/api/v2/workspaces',
  '/api/v2/admin/agents',
  '/api/v2/admin/segments',
  '/api/v2/admin/users/search',
  '/api/v2/tenants',
  '/api/v2/admin/tenants',
  '/api/v2/admin/reports',
  '/api/v2/training',
  '/api/supportReports',
  '/api/v2/users/me/prefs/shortcuts',
  '/api/v2/users/me/prefs/layout',
  '/api/twilio/voice-token',
  '/api/test/execute-flow',
  '/api/test/send-sms',
  '/api/test/send-sip-call',
  '/api/test/sip-call',
];

export function supportExperiencePath(endpoint = '') {
  const path = String(endpoint || '').split('?')[0];
  const normalized = path.startsWith('/api/') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  const lowered = normalized.replace(/\/$/, '') || '/';
  if (lowered.includes('/password') || lowered.includes('pricing') || lowered.includes('billing') || lowered.endsWith('/profile') || lowered.endsWith('/user-tenants') || lowered.endsWith('/tenants/list')) {
    return false;
  }
  return SUPPORT_READ_PREFIXES.some((prefix) => lowered === prefix || lowered.startsWith(`${prefix}/`));
}

export function applySupportTenantToUrl(url) {
  if (readDealerAdminSession()) return url;
  const session = readSupportedTenantSession();
  if (!session?.tenantId || typeof url !== 'string') return url;
  const [path, query = ''] = url.split('?');
  if (!supportExperiencePath(path)) return url;
  const params = new URLSearchParams(query);
  params.set('tenantId', session.tenantId);
  const next = params.toString();
  return next ? `${path}?${next}` : path;
}

export function supportHeadersForEndpoint(endpoint = '') {
  if (readDealerAdminSession()) return {};
  const session = readSupportedTenantSession();
  if (!session?.tenantId || !supportExperiencePath(endpoint)) return {};
  return { [SUPPORT_TENANT_HEADER]: session.tenantId };
}

export function writeSupportedTenantSession(tenant) {
  const tenantId = String(tenant?.tenantId || '').trim();
  if (!tenantId) return;
  try {
    if (!sessionStorage.getItem(PREVIOUS_TENANT_KEY)) {
      sessionStorage.setItem(PREVIOUS_TENANT_KEY, localStorage.getItem('selectedTenantId') || '');
    }
  } catch {
    // The previous tenant is only a convenience on exit.
  }
  sessionStorage.setItem(SUPPORT_SESSION_KEY, JSON.stringify({
    tenantId,
    name: String(tenant?.name || tenant?.displayName || '').trim(),
    accessLevel: tenant?.accessLevel === 'manage' ? 'manage' : 'view',
    algoDeviceSupport: tenant?.algoDeviceSupport === true,
    tenantAlgoEnabled: tenant?.tenantAlgoEnabled === true,
  }));
  try {
    sessionStorage.removeItem(PLATFORM_SUPPORT_DECLINED_KEY);
  } catch {
    // Entering support still works when the decline flag cannot be cleared.
  }
  emit();
}

export function clearSupportedTenantSession() {
  let previous = '';
  const current = readSupportedTenantSession();
  try {
    previous = sessionStorage.getItem(PREVIOUS_TENANT_KEY) || '';
    sessionStorage.removeItem(PREVIOUS_TENANT_KEY);
    if (current?.tenantId) sessionStorage.setItem(PLATFORM_SUPPORT_DECLINED_KEY, current.tenantId);
  } catch {
    previous = '';
  }
  sessionStorage.removeItem(SUPPORT_SESSION_KEY);
  try {
    if (previous) localStorage.setItem('selectedTenantId', previous);
    else localStorage.removeItem('selectedTenantId');
  } catch {
    // Leaving support still clears the session when storage is blocked.
  }
  emit();
}

const SUPPORT_UI_PREFIXES = [
  '/dashboard',
  '/home',
  '/admin/user-management',
  '/admin/workspaces',
  '/admin/shortcuts',
  '/admin/event-system/flows',
  '/admin/event-system/actions',
  '/admin/event-system/devices',
  '/admin/event-system/inbound-rules',
  '/admin/event-system/scheduled',
  '/admin/event-system/simple-actions',
  '/admin/event-system/call-routing',
  '/admin/agent-management',
  '/admin/sites',
  '/support/reports',
  '/support/tenants',
  '/training',
];

export function isSupportExperiencePath(pathname = '') {
  const path = String(pathname || '').split('?')[0].replace(/\/$/, '') || '/';
  if (path === '/admin/tenants') return true;
  return SUPPORT_UI_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function supportAccessLabel(level) {
  return level === 'manage' ? 'Manage settings' : 'View only';
}

export function supportBannerLabel(level) {
  return level === 'manage' ? 'Manage' : 'View';
}

export function requestContextHeaders(supportTenantId) {
  const tenantId = String(supportTenantId || '').trim();
  if (tenantId) return { [SUPPORT_TENANT_HEADER]: tenantId };
  return dealerAdminHeaders();
}
